// 商品明細 ESC/POS 版面 builder
// 版面規則依照 docs/vue商品明細direct_print_data實作引導.md
import { EscPosEncoder, bytesToBase64 } from './escPosEncoder'

/**
 * @typedef {Object} ProductItem
 * @property {string} item_name
 * @property {string} [item_spec]   // 可含 \n 多行
 * @property {number} item_price    // 單價
 * @property {number} item_count
 *
 * @typedef {Object} ComboSubItem
 * @property {string} item_name
 * @property {string} [item_spec]
 * @property {number} item_count
 *
 * @typedef {Object} ComboItem
 * @property {1|true} is_combo
 * @property {string} item_name
 * @property {number} item_price
 * @property {number} item_count
 * @property {ComboSubItem[]} items
 *
 * @typedef {Object} OrderDetailPrintData
 * @property {number} [width]        // 58 或 80；未指定預設 58
 * @property {number} [print_count]
 * @property {string} order_type
 * @property {string} order_number
 * @property {string} [zone]
 * @property {string} [title]
 * @property {string} [order_spec]
 * @property {string} [invoice_carrier]    // 手機載具
 * @property {string} [invoice_vat_number] // 統一編號
 * @property {string} [invoice_love_code]  // 愛心碼
 * @property {(ProductItem|ComboItem)[]} items
 * ...（其餘訂單資訊／金額欄位見文件第 99~152 行）
 */

function lineChars(width) {
  // 未指定 width 時預設 58mm（32 字元）
  return (Number(width) || 58) <= 58 ? 32 : 48
}

// 紙寬 80mm 才放大字級；58mm 維持原樣
function isWide(width) {
  return (Number(width) || 58) > 58
}

function isNonEmpty(v) {
  return v !== undefined && v !== null && String(v).trim() !== ''
}

function last3(orderNumber) {
  if (!orderNumber) return ''
  const lines = String(orderNumber)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  const last = lines.length ? lines[lines.length - 1] : ''
  return last.length > 3 ? last.slice(-3) : last
}

function telLast3(tel) {
  const s = String(tel)
  return s.length > 3 ? s.slice(-3) : s
}

function isDeliveryOrder(data) {
  return (
    data.order_type === '店家外送' ||
    data.order_type === '外送' ||
    data.order_type === '零售宅配'
  )
}

function paymentStatusText(data) {
  let tel = ''
  if (isNonEmpty(data.customer_tel)) tel = `【電話 ${telLast3(data.customer_tel)}`
  return `${tel}${data.payment_status ?? ''}】`
}

function itemTotal(item) {
  return Number(item.item_price || 0) * Number(item.item_count || 0)
}

// 主商品左側字串：{數量}x{名稱}（備註）$單價
function mainItemLeft(item) {
  const note = isNonEmpty(item.item_note) ? `（${item.item_note}）` : ''
  const price = isNonEmpty(item.item_price) ? `$${item.item_price}` : ''
  return `${item.item_count}x${item.item_name}${note}${price}`
}

function dashLine(enc, width) {
  enc.align('left').line('-'.repeat(lineChars(width)))
}
// 顯示寬度：中文／全形字佔 2 欄，其餘 1 欄（用於靠右對齊）
function charCols(code) {
  if (
    (code >= 0x1100 && code <= 0x115f) || // Hangul Jamo
    (code >= 0x2e80 && code <= 0xa4cf) || // CJK 部首～統一表意
    (code >= 0xac00 && code <= 0xd7a3) || // Hangul 音節
    (code >= 0xf900 && code <= 0xfaff) || // CJK 相容
    (code >= 0xfe30 && code <= 0xfe4f) || // CJK 相容形式
    (code >= 0xff00 && code <= 0xff60) || // 全形 ASCII
    (code >= 0xffe0 && code <= 0xffe6)
  ) return 2
  return 1
}
function displayWidth(str) {
  let w = 0
  for (const ch of String(str)) w += charCols(ch.codePointAt(0))
  return w
}
// 逐字斷行：第一行可用寬度 firstCols、其餘行為 restCols（用於主商品名過長時）
function wrapByWidthFirst(text, firstCols, restCols) {
  const lines = []
  let cur = ''
  let curW = 0
  let limit = firstCols
  for (const ch of String(text)) {
    const cw = charCols(ch.codePointAt(0))
    if (curW + cw > limit && cur !== '') {
      lines.push(cur)
      cur = ''
      curW = 0
      limit = restCols
    }
    cur += ch
    curW += cw
  }
  if (cur !== '' || lines.length === 0) lines.push(cur)
  return lines
}
// 主商品行：左品名 + 靠右總價同一行，字級高 2 寬 1。
// 品名過長時，總價維持第一行最右，品名逐字換行；續行斷點與第一行相同（保留右側價格欄）。
// 寬度倍率為 1，水平欄數與一般字相同，靠右補白計算不變。
function mainItemRow(enc, left, right, width) {
  const wide = isWide(width)
  // 寬2 時每字佔雙倍橫向格，可用欄數減半
  const cols = wide ? lineChars(width) / 2 : lineChars(width)
  enc.align('left').size(wide ? 2 : 1, 2)
  if (!right) {
    enc.line(left).size(1, 1)
    return
  }
  const lw = displayWidth(left)
  const rw = displayWidth(right)
  if (lw + 1 + rw <= cols) {
    enc.line(left + ' '.repeat(cols - lw - rw) + right)
  } else {
    // 第一行保留價格欄（rw + 至少 1 格間隔），品名在其左側起逐字換行
    const firstCols = Math.max(1, cols - rw - 1)
    // 續行斷點與第一行相同（同樣保留右側價格欄），避免續行衝到價格欄下方
    const segs = wrapByWidthFirst(left, firstCols, firstCols)
    const firstW = displayWidth(segs[0])
    enc.line(segs[0] + ' '.repeat(Math.max(1, cols - firstW - rw)) + right)
    for (let i = 1; i < segs.length; i++) enc.line(segs[i])
  }
  enc.size(1, 1)
}
function centerTitle(enc, txt) {
  enc.align('center').size(2, 2).line(txt).size(1, 1).align('left')
}
function bigLine(enc, txt) {
  enc.align('left').size(2, 2).line(txt).size(1, 1)
}
function rightLine(enc, txt) {
  enc.align('right').line(txt).align('left')
}
function optionalLine(enc, label, value) {
  if (isNonEmpty(value)) enc.line(`${label}: ${value}`)
}
function textLine(enc, value) {
  if (isNonEmpty(value)) enc.line(String(value))
}
// 資訊區塊欄位：80mm 高2寬1，58mm 原樣
function infoLine(enc, label, value, width) {
  if (!isNonEmpty(value)) return
  if (isWide(width)) enc.size(1, 2).line(`${label}: ${value}`).size(1, 1)
  else enc.line(`${label}: ${value}`)
}
// 時間欄位：80mm 標題高2寬1、值高2寬2；58mm 原樣
function timeLine(enc, label, value, width) {
  if (!isNonEmpty(value)) return
  if (isWide(width)) {
    enc.size(1, 2).text(`${label}: `).size(2, 2).text(String(value)).line('').size(1, 1)
  } else {
    enc.line(`${label}: ${value}`)
  }
}
// 依顯示寬度將一段文字斷成多行（中文 2 欄、ASCII 1 欄）
// 優先在空白處斷行；單一詞超寬時才硬斷字元，避免交給印表機自動換行
function wrapByWidth(text, cols) {
  const lines = []
  let cur = ''
  let curW = 0
  const flush = () => {
    if (cur !== '') lines.push(cur)
    cur = ''
    curW = 0
  }
  for (const token of String(text).split(' ')) {
    if (token === '') continue
    const sep = curW > 0 ? 1 : 0
    const tokenW = displayWidth(token)
    if (curW + sep + tokenW <= cols) {
      cur += (sep ? ' ' : '') + token
      curW += sep + tokenW
    } else {
      flush()
      if (tokenW > cols) {
        // 詞本身超過整行寬度 → 逐字硬斷
        for (const ch of token) {
          const cw = charCols(ch.codePointAt(0))
          if (curW + cw > cols) flush()
          cur += ch
          curW += cw
        }
      } else {
        cur = token
        curW = tokenW
      }
    }
  }
  flush()
  return lines
}

// 規格／子項規格：保留 pad 縮排；超過紙寬時手動斷行，續行仍補相同縮排
function indentedLines(enc, value, pad, width, tall = false) {
  const big = tall && isWide(width) // 高2寬1
  // 80mm 商品名為倍寬(2,2)，規格為正常寬(1,2)；縮排空白加倍才能對齊倍寬商品的縮排層級
  const effPad = big ? pad + pad : pad
  const cols = Math.max(1, lineChars(width) - displayWidth(effPad))
  for (const seg of String(value).split('\n')) {
    if (seg.trim() === '') continue
    for (const wrapped of wrapByWidth(seg, cols)) {
      if (big) enc.size(1, 2).line(`${effPad}${wrapped}`).size(1, 1)
      else enc.line(`${effPad}${wrapped}`)
    }
  }
}

function printOrderSpec(enc, data) {
  if (!isNonEmpty(data.order_spec)) return
  dashLine(enc, data.width)
  if (isWide(data.width)) enc.size(1, 2).line('全單規格:').size(1, 1)
  else enc.line('全單規格:')
  indentedLines(enc, data.order_spec, '  ', data.width, true)
}

// 一般商品：主商品行（名稱後接備註括弧與單價）+ (有規格時) 半行間距 + 規格
function productItem(enc, data, item) {
  mainItemRow(enc, mainItemLeft(item), String(itemTotal(item)), data.width)
  if (isNonEmpty(item.item_spec)) {
    enc.smallGap() // 商品列與規格間留半行間距做區分
    indentedLines(enc, item.item_spec, '  ', data.width, true)
  }
}

// 組合商品：主商品行（名稱後接單價）+ (有子項時) 半行間距 + 子項與其規格
function comboItem(enc, data, item) {
  mainItemRow(enc, mainItemLeft(item), String(itemTotal(item)), data.width)
  const subs = item.items || []
  if (subs.length) enc.smallGap()
  for (const sub of subs) {
    const subLine = `  ${sub.item_count}x${sub.item_name}`
    if (isWide(data.width)) enc.size(2, 2).line(subLine).size(1, 1)
    else enc.line(subLine)
    if (isNonEmpty(sub.item_spec)) indentedLines(enc, sub.item_spec, '    ', data.width, true)
  }
}

function printItems(enc, data) {
  const items = data.items || []
  if (!items.length) return
  dashLine(enc, data.width)
  for (const item of items) {
    if (item.is_combo === 1 || item.is_combo === true) {
      comboItem(enc, data, item)
    } else {
      productItem(enc, data, item)
    }
  }
}

function printAmountSummary(enc, data) {
  dashLine(enc, data.width)
  enc.line(`小計: ${data.item_counts ?? ''} 項 金額: $${data.subtotal ?? ''}`)
  const right = (label, value) => {
    if (isNonEmpty(value)) rightLine(enc, `${label}: ${value}`)
  }
  right('服務費', data.service_fee)
  right('現場折價', data.on_site_discount_off)
  right('現場減價', data.on_site_discount)
  right('免稅減價', data.duty_free_discount)
  right('服務費', data.after_discount_service_fee)
  right('額外費用', data.extra_income)
  right('刷卡手續費', data.credit_card_fee)
  right('運費', data.shipping_fee)
  right('紅利積分減價', data.point_discount)
  if (isNonEmpty(data.point_balance)) rightLine(enc, `(紅利積分餘額 ${data.point_balance} 點)`)
  if (isWide(data.width))
    enc.align('right').size(2, 2).line(`總計: ${data.total_amount ?? ''}`).size(1, 1).align('left')
  else rightLine(enc, `總計: ${data.total_amount ?? ''}`)
}

function printFooterInfo(enc, data) {
  dashLine(enc, data.width)
  optionalLine(enc, '付款方式', data.payment_method)
  enc.line(`訂單編號: ${data.order_number ?? ''}`)
  enc.line(`訂單時間: ${data.order_time ?? ''}`)
  if (isNonEmpty(data.staff)) {
    enc.line(`訂單狀態: ${data.order_status ?? ''}(${data.staff})`)
  } else {
    enc.line(`訂單狀態: ${data.order_status ?? ''}`)
  }
  textLine(enc, data.commission_code_tail)
}

// 單份版面（encoder 與 text sink 共用，sink 會忽略 align/size/bold）
// 註：會員線上點餐建立訂單，不會有棄單/異動單，故不處理。
function buildOneCopy(enc, data) {
  centerTitle(enc, isNonEmpty(data.title) ? data.title : '商品明細')
  dashLine(enc, data.width)

  bigLine(enc, `${data.order_type ?? ''}${last3(data.order_number)}`)
  if (isNonEmpty(data.zone) && String(data.zone).trim() !== '-1') bigLine(enc, data.zone)
  if (isNonEmpty(data.payment_status)) {
    if (isWide(data.width))
      enc.align('right').size(2, 2).line(paymentStatusText(data)).size(1, 1).align('left')
    else rightLine(enc, paymentStatusText(data))
  }

  timeLine(enc, '用餐時間', data.meal_time, data.width)
  timeLine(enc, '取餐時間', data.get_meal_time, data.width)
  timeLine(enc, '送達時間', data.delivery_time, data.width)
  timeLine(enc, '取貨時間', data.get_order_time, data.width)
  // 外送/宅配不印顧客姓名與電話
  if (!isDeliveryOrder(data)) {
    infoLine(enc, '顧客姓名', data.customer_name, data.width)
    infoLine(enc, '顧客電話', data.customer_tel, data.width)
  }
  infoLine(enc, '聯絡人', data.recipient, data.width)
  infoLine(enc, '聯絡電話', data.contact_phone, data.width)
  infoLine(enc, '外送地址', data.delivery_address, data.width)
  textLine(enc, data.buyer_info)
  infoLine(enc, '顧客備註', data.custom_comment, data.width)
  infoLine(enc, '訂單備註', data.order_comment, data.width)
  infoLine(enc, '手機載具', data.invoice_carrier, data.width)
  infoLine(enc, '統一編號', data.invoice_vat_number, data.width)
  infoLine(enc, '愛心碼', data.invoice_love_code, data.width)

  printOrderSpec(enc, data)
  printItems(enc, data)
  printAmountSummary(enc, data)
  printFooterInfo(enc, data)
}

/** @param {OrderDetailPrintData} data @returns {Uint8Array} */
export function buildOrderDetailEscPosBytes(data) {
  const enc = new EscPosEncoder()
  enc.initialize()
  const copyCount = Number(data.print_count) > 0 ? Number(data.print_count) : 1
  for (let i = 0; i < copyCount; i++) {
    buildOneCopy(enc, data)
    enc.newline(4) // 結尾補空白行再切紙（避免最後一行被切刀切到）
    enc.cut()
  }
  return enc.encode()
}

/** @param {OrderDetailPrintData} data @returns {string} Base64 */
export function buildOrderDetailDirectPrintData(data) {
  return bytesToBase64(buildOrderDetailEscPosBytes(data))
}

// 純文字預覽（驗收用）：與 bytes 共用同一套版面邏輯，忽略字級/對齊
class TextSink {
  constructor() {
    this._lines = []
    this._cur = ''
  }
  initialize() {
    return this
  }
  align() {
    return this
  }
  bold() {
    return this
  }
  size() {
    return this
  }
  text(s = '') {
    this._cur += String(s)
    return this
  }
  line(s = '') {
    this._cur += String(s)
    this._lines.push(this._cur)
    this._cur = ''
    return this
  }
  newline(n = 1) {
    for (let i = 0; i < n; i++) {
      this._lines.push(this._cur)
      this._cur = ''
    }
    return this
  }
  smallGap() {
    // 預覽用半行間距，以空白行近似呈現
    return this.newline(1)
  }
  cut() {
    this._lines.push('[cut]')
    return this
  }
  toString() {
    return this._lines.join('\n')
  }
}

export function renderOrderDetailText(data) {
  const sink = new TextSink()
  const copyCount = Number(data.print_count) > 0 ? Number(data.print_count) : 1
  for (let i = 0; i < copyCount; i++) {
    buildOneCopy(sink, data)
    sink.newline(6)
    sink.cut()
  }
  return sink.toString()
}

// 手動驗證用範例
export const sampleOrderDetailPrintData = {
  width: 58,
  print_count: 1,
  order_type: '內用',
  order_number: 'A000123',
  zone: 'A1',
  order_time: '2026-06-16 18:20',
  order_status: '已付款',
  staff: '店員A',
  meal_time: '06-16 18:30',
  customer_name: '王小明',
  customer_tel: '0912345678',
  payment_status: '已付款',
  buyer_info: '統編:12345678',
  custom_comment: '少冰',
  order_comment: '先做飲料',
  invoice_carrier: '/ABC1234',
  order_spec: '不加香菜',
  title: '商品明細',
  items: [
    { item_name: '漢堡', item_note: '不要加醬', item_spec: '少冰\n無糖', item_price: 35, item_count: 2 },
    {
      is_combo: 1,
      item_name: '雙人套餐',
      item_price: 299,
      item_count: 1,
      items: [
        { item_name: '漢堡', item_spec: '加起司', item_count: 1 },
        { item_name: '紅茶', item_spec: '微糖', item_count: 2 },
      ],
    },
  ],
  item_counts: 5, // 漢堡 2 + 套餐子商品(1+2)=3 → 5
  subtotal: '459',
  service_fee: '46',
  on_site_discount: '-10',
  total_amount: '495',
  payment_method: '信用卡',
  commission_code_tail: '1234',
}
